import React, { useMemo, useState } from "react";
import { Combobox, UnstyledButton, useCombobox } from "@mantine/core";

function TenantLabel({ id, name, idColumnWidth }) {
	return (
		<span
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				minWidth: 0,
				overflow: "hidden",
			}}
		>
			<span
				style={{
					fontFamily: "monospace",
					width: idColumnWidth,
					flexShrink: 0,
				}}
			>
				{id}
			</span>
			<span
				style={{
					minWidth: 0,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
				}}
			>
				{name}
			</span>
		</span>
	);
}

const TARGET_STYLE = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 8,
	width: "fit-content",
	minWidth: 220,
	maxWidth: 400,
	height: 36,
	padding: "0 8px",
	border: "1px solid var(--mantine-color-gray-4)",
	borderRadius: "var(--mantine-radius-sm)",
	cursor: "pointer",
	fontSize: "var(--mantine-font-size-sm)",
};

const LABEL_WRAPPER_STYLE = {
	flex: 1,
	minWidth: 0,
	overflow: "hidden",
};

// Searchable tenant picker; filters by id or name as the user types.
const TenantSelect = ({ tenants, value, onChange }) => {
	const combobox = useCombobox({
		onDropdownOpen: () => combobox.focusSearchInput(),
		onDropdownClose: () => combobox.resetSelectedOption(),
	});
	const [search, setSearch] = useState("");

	const sortedTenants = useMemo(
		() => [...(tenants ?? [])].sort((a, b) => a.id - b.id),
		[tenants]
	);

	const idColumnWidth = useMemo(
		() =>
			`${Math.max(1, ...sortedTenants.map((t) => String(t.id).length))}ch`,
		[sortedTenants]
	);

	const selectedTenant = sortedTenants.find(
		(t) => String(t.id) === String(value)
	);

	const filteredTenants = useMemo(() => {
		const query = search.trim().toLowerCase();
		if (!query) return sortedTenants;
		return sortedTenants.filter(
			(t) =>
				String(t.id).includes(query) ||
				t.name.toLowerCase().includes(query)
		);
	}, [sortedTenants, search]);

	return (
		<Combobox
			store={combobox}
			onOptionSubmit={(newValue) => {
				onChange(newValue);
				setSearch("");
				combobox.closeDropdown();
			}}
		>
			<Combobox.Target>
				<UnstyledButton
					type="button"
					onClick={() => combobox.toggleDropdown()}
					style={TARGET_STYLE}
				>
					<span style={LABEL_WRAPPER_STYLE}>
						{selectedTenant ? (
							<TenantLabel
								id={selectedTenant.id}
								name={selectedTenant.name}
								idColumnWidth={idColumnWidth}
							/>
						) : (
							<span
								style={{ color: "var(--mantine-color-dimmed)" }}
							>
								Select firm
							</span>
						)}
					</span>
					<Combobox.Chevron style={{ flexShrink: 0 }} />
				</UnstyledButton>
			</Combobox.Target>

			<Combobox.Dropdown>
				<Combobox.Search
					value={search}
					onChange={(event) => setSearch(event.currentTarget.value)}
					placeholder="Search by name or ID"
				/>
				<Combobox.Options style={{ maxHeight: 300, overflowY: "auto" }}>
					{filteredTenants.length > 0 ? (
						filteredTenants.map((t) => (
							<Combobox.Option value={String(t.id)} key={t.id}>
								<TenantLabel
									id={t.id}
									name={t.name}
									idColumnWidth={idColumnWidth}
								/>
							</Combobox.Option>
						))
					) : (
						<Combobox.Empty>No firms found</Combobox.Empty>
					)}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};

export default TenantSelect;
